import { createContext, useContext, useCallback, useState, useEffect } from 'react';
import { logRejectionsToSentry } from '../Sentry';
import { useUserData } from '../UserProvider/UserProvider';
import { api3Call } from '../schema/api3/client';
import * as api3 from '../schema/api3/endpoints';
import { Api3Favourite } from '../schema/api3/types/Api3Favourite';
import { useFetch } from '../services/NetworkService';
import { ChildrenOnly } from '../types/ChildrenOnly';
import { returnVoid } from '../util/returnVoid';

type AddFavourite = (id: string, profileType: 'Practice' | 'Practitioner') => void;

export type RemoveFavourite = (id: string) => void;

export const FavouritesContext = createContext<{
  favourites: Array<Api3Favourite>;
  refetchFavourites: VoidFunction;
  addFavourite: AddFavourite;
  removeFavourite: RemoveFavourite;
}>({
  favourites: [],
  refetchFavourites: returnVoid,
  addFavourite: returnVoid,
  removeFavourite: returnVoid,
});

export const useFavourites = () => useContext(FavouritesContext);

export const FavouritesProvider = (props: ChildrenOnly) => {
  const [favourites, setFavourites] = useState<Array<Api3Favourite>>([]);
  const userData = useUserData();
  const isLoggedIn = !!userData.data;

  const fetch = useFetch();

  const refetchFavourites = useCallback(async () => {
    if (isLoggedIn) {
      const result = await api3Call(fetch, api3.account.getFavourites());
      setFavourites(result.data);
    } else {
      setFavourites([]);
    }
  }, [setFavourites, fetch, isLoggedIn]);

  const addFavourite = useCallback(
    async (id: string, profileType: 'Practice' | 'Practitioner') => {
      await api3Call(fetch, api3.account.addFavourite(id, profileType));
      await refetchFavourites();
    },
    [fetch, refetchFavourites],
  );

  const removeFavourite = useCallback(
    async (id: string) => {
      await api3Call(fetch, api3.account.deleteFavourite(id));
      await refetchFavourites();
    },
    [fetch, refetchFavourites],
  );

  useEffect(() => {
    if (isLoggedIn) {
      logRejectionsToSentry('Fetching favourites', () => refetchFavourites());
    }
  }, [isLoggedIn, refetchFavourites]);

  return (
    <FavouritesContext.Provider
      value={{ favourites, refetchFavourites, addFavourite, removeFavourite }}
    >
      {props.children}
    </FavouritesContext.Provider>
  );
};
